import React, { useState, useEffect, useRef } from "react";
import LabelAndToggle from "./LabelAndToggle";
import { CheckAnswer, CogIcon, TrashIcon } from "../../../../../assets/icons";
import { v4 as uuidv4 } from 'uuid';
import { useDrag, useDrop } from 'react-dnd';

const ItemTypes = {
  ELEMENT: 'element',
};

const areOptionsEqual = (first, second) => {
  if (!Array.isArray(first) || !Array.isArray(second)) return false;
  return JSON.stringify(first) === JSON.stringify(second);
};

function OptionItem({ answer, index, handleOptionChange, toggleCheckOption, deleteOption, moveOption, visibility }) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ELEMENT,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.ELEMENT,
    drop: (item) => {
      moveOption(item.index, index);
    },
  });

  return (
    <div key={index} className="item-list-flex-design" ref={(node) => drag(drop(node))}>
      <div
        className={`flex-third-col-design ${answer.isChecked ? "active" : ""
          }`}
      >
        <div className="icon-flex-third-col-design">
          <CogIcon />
        </div>
        <div className="filed-design-form flex-full">
          <input
            type="text"
            className="input-design-form"
            placeholder="Write here..."
            value={answer.value}
            onChange={(e) => handleOptionChange(index, e.target.value)}
          />
        </div>
        {visibility &&
          <div
            onClick={() => toggleCheckOption(index)}
            className="question-correct-answer">
            <CheckAnswer />
          </div>
        }
        <div
          onClick={() => deleteOption(index)}
          className="button-delete"
        >
          <TrashIcon />
        </div>
      </div>
    </div>
  );
}

// pass the label if needed
function LabelAndMultipleChoice({ defaultValue, onChange, options, visibility = false }) {
  const onChangeRef = useRef(onChange);

  const [answers, setOptions] = useState(
    Array.isArray(defaultValue)
      ? defaultValue
      : [{
        value: "currently employed",
        isChecked: false,
      },
      {
        value: "Retired",
        isChecked: false,
      },]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (Array.isArray(defaultValue)) {
      setOptions((prev) => (areOptionsEqual(prev, defaultValue) ? prev : defaultValue));
    }
  }, [defaultValue]);

  const commitAnswers = (updater) => {
    setOptions((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (areOptionsEqual(prev, next)) return prev;
      onChangeRef.current?.(next);
      return next;
    });
  };

  const handleOptionChange = (index, newValue) => {
    commitAnswers((prev) =>
      prev.map((answer, i) =>
        i === index ? { ...answer, value: newValue } : answer
      )
    );
  };

  const toggleCheckOption = (index) => {
    commitAnswers((prev) =>
      prev.map((answer, i) =>
        i === index ? { ...answer, isChecked: !answer.isChecked } : answer
      )
    );
  };

  const deleteOption = (index) => {
    commitAnswers((prev) => prev.filter((_, i) => i !== index));
  };

  const addNewOption = () => {
    commitAnswers((prev) => [
      ...prev,
      {
        id: uuidv4(),
        value: "",
        isChecked: false,
      },
    ]);
  };

  const moveOption = (dragIndex, hoverIndex) => {
    commitAnswers((prev) => {
      const dragOption = prev[dragIndex];
      if (!dragOption) return prev;
      const newOptions = [...prev];
      newOptions.splice(dragIndex, 1);
      newOptions.splice(hoverIndex, 0, dragOption);
      return newOptions;
    });
  };

  return (
    <div className="item-design-form-path">
      <div className="list-flex-design list-question-design">
        {visibility && <LabelAndToggle title="Set correct answer" checked={true} />}
        {answers && answers.map((answer, index) => (
          <OptionItem
            key={index}
            answer={answer}
            index={index}
            handleOptionChange={handleOptionChange}
            toggleCheckOption={toggleCheckOption}
            deleteOption={deleteOption}
            moveOption={moveOption}
            visibility={visibility}
          />
        ))}
      </div>
      <div className="cover-add-btn-design">
        <button
          onClick={addNewOption}
          type="button"
          className="button-add btn-add-question"
        >
          Add
        </button>
      </div>
    </div>
  );
}

export default LabelAndMultipleChoice;
